import { useMemo } from 'react';

import { Icon, Spacer, Toggle, Typography, useTheme } from '@aircall/tractor';
import { GridItem, GridLayout } from '@components/GridLayout/GridLayout';
import { ListItem } from '@components/ListItem/ListItem';
import { UserFullFragment, UserFullFragment_phoneNumbers } from '@generated/UserFullFragment';
import { getFlagName } from '@helpers/countries.helpers';
import { useDeletePhoneNumber } from '@hooks/useDeletePhoneNumber';
import { useToggleForwardPhoneNumber } from '@hooks/useToggleForwardPhoneNumber';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';

interface PhoneNumberRowProps {
  user: UserFullFragment;
  phoneNumber: UserFullFragment_phoneNumbers;
}

export function PhoneNumberRow({ user, phoneNumber }: PhoneNumberRowProps) {
  const { t } = useTranslation();
  const { space } = useTheme();

  const { updatePhoneNumber } = useToggleForwardPhoneNumber({ user, phoneNumber });
  const { deletePhoneNumber } = useDeletePhoneNumber({ user, phoneNumber });
  const { parsedPhoneNumber, flagName } = useMemo(() => {
    const parsedPhone = parsePhoneNumber(`+${phoneNumber.phoneNumber}`);

    const parsedFlag = getFlagName(parsedPhone.country!);
    return { parsedPhoneNumber: parsedPhone, flagName: parsedFlag };
  }, [phoneNumber]);

  return (
    <ListItem
      dropdownOptions={[
        {
          title: t('user_profile.call_preferences.forward_call_phone.remove_button'),
          callback: deletePhoneNumber,
        },
      ]}
      height={space.xxxl}
      data-test={`phone-number-row-${phoneNumber.ID}`}
    >
      <GridLayout p='s' alignItems='center' gridGap={48} position='relative'>
        <GridItem xs={12} md={4}>
          <Spacer alignItems='center' space='xxs'>
            <Icon component={flagName} size={space.m} />
            <Typography>{parsedPhoneNumber.formatInternational()}</Typography>
          </Spacer>
        </GridItem>
        <GridItem xs={12} md={6} lg={6}>
          <Spacer space='s' alignItems='center'>
            <Toggle
              size='small'
              checked={phoneNumber.isEnabled}
              onChange={updatePhoneNumber}
              data-test={`phone-number-toggle-${phoneNumber.ID}`}
            />
            <Typography color='text.dark'>
              {t('user_profile.call_preferences.forward_call_phone.activate_number')}
            </Typography>
          </Spacer>
        </GridItem>
      </GridLayout>
    </ListItem>
  );
}
